import type { ContentFile } from "~/models/Content/schemas"

import type { DomParser } from "~/plugins/dom-parser"
import type { Article, Relation } from "~/types/article"
import type { Audio, Caption, Quiz, Video } from "~/types/media"
import { is } from "valibot"
import { ContentType } from "~/models/Content/ContentType"
import { ImageAssetSchema } from "~/models/Content/schemas"
import { createHtmlFormatter } from "~/utilities/dom-parsing"
import createMathTask from "./create-mathtask"

const isRelation = isContentType(
  ContentType.SidenoteWhatIs,
  ContentType.SidenoteSource,
  ContentType.SidenoteLegalSource,
  ContentType.SidenotePerson,
  ContentType.SidenoteExplainer,
  ContentType.SidenoteTerm,
  ContentType.Sidenote,
)

export function formatData(article: Article, parser: DomParser): Article {
  const { id, template, bg, color, relatedItems } = article

  const { video, audio, quiz, files, captions } = (
    relatedItems || []
  ).reduce<{
    video?: Video
    audio?: Audio
    quiz?: Quiz
    files: ContentFile[]
    captions: Caption[]
  }>(
    (acc, item) => {
      if (isContentType(ContentType.Caption)(item)) {
        acc.captions.push(item)
      } else if (isContentType(ContentType.File)(item)) {
        acc.files.push(item)
      } else if (isContentType(ContentType.Video)(item)) {
        acc.video = item
      } else if (isContentType(ContentType.Audio)(item)) {
        acc.audio = item
      } else if (isContentType(ContentType.Quiz)(item)) {
        acc.quiz = item
      }
      return acc
    },
    { captions: [], files: [] },
  )

  if (video) {
    video.captions = captions
  }

  const articleSidenotes = relatedItems.filter(isRelation)

  const mathRelations =
    article.children
      ?.filter(isContentType(ContentType.MathTaskIbexa))
      ?.flatMap(m => m.relatedItems ?? [])
      ?.filter(isRelation) ?? []

  const sidenotes = [...articleSidenotes, ...mathRelations].map(
    enrichSidenoteWithMedia,
  )

  const mappedArticleImage = (): Article["img"] => {
    if (!article.img)
      return undefined

    const articleImage = relatedItems.find(
      r => r.contentId === article.img?.id,
    )

    if (articleImage && is(ImageAssetSchema, articleImage)) {
      return {
        ...article.img,
        detailedDescription: articleImage.detailedDescription,
      }
    } else {
      return article.img
    }
  }

  /** Function to format links and references. This also mutates relations! */
  const formatHtml = createHtmlFormatter(sidenotes, parser)

  const mathTasks = article.children?.filter(isContentType(ContentType.MathTaskIbexa))

  const mathTask = createMathTask(id, mathTasks, formatHtml)

  const intro = {
    html: formatHtml(article.intro?.html ?? ""),
  }

  const body = {
    html: formatHtml(article.body?.html ?? ""),
  }

  return {
    ...article,
    intro,
    body,
    // Derived fields
    img: mappedArticleImage(),
    template:
      `U${onlyChars(convertToCase(template, "pascal"))}Article`,

    bg: bg === "default" ? "paper" : bg,
    color: color === "default" ? "black" : color,
    relations: sidenotes,
    files,
    video,
    audio,
    quiz,
    mathTask,

    // Required missing properties
    colorTag: undefined,
    hidden: false,
    restricted: false,
    frontendHref: undefined,
    subjectPageTitlePosition: undefined,
  }
}

function enrichSidenoteWithMedia(sidenote: Relation): Relation {
  const video = sidenote.relatedItems?.find(isContentType(ContentType.Video))

  const imageItem =
    sidenote.relatedItems?.find(
      item =>
        item.contentTypeIdentifier === "image" &&
        item.contentId === sidenote.image?.id,
    ) ?? sidenote.image

  const image =
    imageItem && is(ImageAssetSchema, imageItem)
      ? {
          uri: imageItem.src,
          height: imageItem.height,
          width: imageItem.width,
          alternativeText: imageItem.alt,
          variation: { uri: imageItem.src },
          detailedDescription: imageItem.detailedDescription,
        }
      : undefined

  return {
    ...sidenote,
    video: video ?? null,
    image,
  }
}
